import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import SignaturePad from '../SignaturePad';
import ManagerSelect from '../ManagerSelect';
import { RichText, getFormText, NO_AUTOFILL } from '../PolicyText';

const UniformForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const { user } = useAuth();
  // TKT-199: dropped bootSize per Naomi — Paramount doesn't issue boots
  // through this form. Remaining sizing fields (shirt, pant, jacket,
  // hat, quantity) cover the actual uniform issuance flow.
  const [formData, setFormData] = useState({
    employeeName: '',
    managerName: '',
    shirtSize: '',
    pantSize: '',
    jacketSize: '',
    hatRequired: 'no',
    quantity: 1,
    notes: ''
  });
  const [employeeSignature, setEmployeeSignature] = useState(null);
  const [managerSignature, setManagerSignature] = useState(null);
  const [errors, setErrors] = useState({});
  // TKT-273: office reported the "complete required information" prompt
  // "persisting" with no clue which field was the problem - the two
  // signature pads have no visual required marker, so a missing signature
  // was easy to miss below the fold. Track which labels are missing so we
  // can call them out explicitly instead of relying on inline field errors.
  const [missingFieldLabels, setMissingFieldLabels] = useState([]);

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData((prev) => ({ ...prev, employeeName: fullName }));
    }
  }, [user]);

  const labels = language === 'es' ? {
    title: 'Formulario de Uniforme',
    employeeName: 'Nombre del Empleado',
    managerName: 'Nombre del Gerente',
    shirtSize: 'Talla de Camisa',
    pantSize: 'Talla de Pantalón',
    jacketSize: 'Talla de Chaqueta',
    hatRequired: '¿Requiere Gorra?',
    yes: 'Sí',
    no: 'No',
    quantity: 'Cantidad',
    notes: 'Notas',
    employeeSignature: 'Firma del Empleado',
    managerSignature: 'Firma del Gerente',
    save: 'Guardar Formulario',
    required: 'Obligatorio',
    signatureRequired: 'Se requiere firma',
    fixErrors: 'Complete los campos resaltados antes de guardar: '
  } : {
    title: 'Uniform Form',
    employeeName: 'Employee Name',
    managerName: 'Manager Name',
    shirtSize: 'Shirt Size',
    pantSize: 'Pant Size',
    jacketSize: 'Jacket Size',
    hatRequired: 'Hat Required?',
    yes: 'Yes',
    no: 'No',
    quantity: 'Quantity',
    notes: 'Notes',
    employeeSignature: 'Employee Signature',
    managerSignature: 'Manager Signature',
    save: 'Save Form',
    required: 'Required',
    signatureRequired: 'Signature required',
    fixErrors: 'Please complete the highlighted fields below before saving: '
  };

  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
      setMissingFieldLabels((prev) => prev.filter((l) => l !== labels[field]));
    }
  };

  const validate = () => {
    const newErrors = {};
    const missing = [];
    ['employeeName', 'managerName', 'shirtSize'].forEach((field) => {
      if (!String(formData[field] || '').trim()) {
        newErrors[field] = labels.required;
        missing.push(labels[field]);
      }
    });
    if (!employeeSignature) {
      newErrors.employeeSignature = labels.signatureRequired;
      missing.push(labels.employeeSignature);
    }
    if (!managerSignature) {
      newErrors.managerSignature = labels.signatureRequired;
      missing.push(labels.managerSignature);
    }
    setErrors(newErrors);
    setMissingFieldLabels(missing);
    return missing.length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    onSubmit({ ...formData, employeeSignature, managerSignature }, employeeSignature);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      {/* Legacy PL Hub deduction authorization (employeeForms.text.uniform) */}
      <Typography variant="body2" sx={{ mb: 3 }}>
        <RichText text={getFormText(language, 'text.uniform.authorization')} values={{ name: formData.employeeName }} />
      </Typography>

      {missingFieldLabels.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {labels.fixErrors}{missingFieldLabels.join(', ')}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.employeeName}
            value={formData.employeeName}
            onChange={(event) => handleChange('employeeName', event.target.value)}
            required
            disabled={disabled}
            error={!!errors.employeeName}
            helperText={errors.employeeName}
            inputProps={{ ...NO_AUTOFILL, 'aria-label': labels.employeeName }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ManagerSelect
            label={labels.managerName}
            value={formData.managerName}
            onChange={(managerName) => handleChange('managerName', managerName)}
            required
            disabled={disabled}
            error={!!errors.managerName}
            helperText={errors.managerName}
            language={language}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth required error={!!errors.shirtSize}>
            <InputLabel>{labels.shirtSize}</InputLabel>
            <Select
              value={formData.shirtSize}
              label={labels.shirtSize}
              onChange={(event) => handleChange('shirtSize', event.target.value)}
              disabled={disabled}
              inputProps={{ 'aria-label': labels.shirtSize }}
            >
              {sizeOptions.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label={labels.pantSize}
            value={formData.pantSize}
            onChange={(event) => handleChange('pantSize', event.target.value)}
            disabled={disabled}
            inputProps={{ 'aria-label': labels.pantSize }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>{labels.jacketSize}</InputLabel>
            <Select
              value={formData.jacketSize}
              label={labels.jacketSize}
              onChange={(event) => handleChange('jacketSize', event.target.value)}
              disabled={disabled}
              inputProps={{ 'aria-label': labels.jacketSize }}
            >
              <MenuItem value="">-</MenuItem>
              {sizeOptions.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>{labels.hatRequired}</InputLabel>
            <Select
              value={formData.hatRequired}
              label={labels.hatRequired}
              onChange={(event) => handleChange('hatRequired', event.target.value)}
              disabled={disabled}
              inputProps={{ 'aria-label': labels.hatRequired }}
            >
              <MenuItem value="yes">{labels.yes}</MenuItem>
              <MenuItem value="no">{labels.no}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label={labels.quantity}
            value={formData.quantity}
            onChange={(event) => handleChange('quantity', event.target.value)}
            disabled={disabled}
            inputProps={{ min: 1, 'aria-label': labels.quantity }}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={labels.notes}
            value={formData.notes}
            onChange={(event) => handleChange('notes', event.target.value)}
            disabled={disabled}
            inputProps={{ 'aria-label': labels.notes }}
          />
        </Grid>
      </Grid>

      <Typography variant="body2" sx={{ mt: 3 }}>
        {getFormText(language, 'text.uniform.declaration')}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <SignaturePad
        onSignature={(sig) => {
          setEmployeeSignature(sig);
          if (sig) {
            setErrors((prev) => ({ ...prev, employeeSignature: '' }));
            setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.employeeSignature));
          }
        }}
        label={labels.employeeSignature}
        language={language}
      />
      {errors.employeeSignature && <Typography color="error" variant="caption">{errors.employeeSignature}</Typography>}

      <Divider sx={{ my: 3 }} />

      <SignaturePad
        onSignature={(sig) => {
          setManagerSignature(sig);
          if (sig) {
            setErrors((prev) => ({ ...prev, managerSignature: '' }));
            setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.managerSignature));
          }
        }}
        label={labels.managerSignature}
        language={language}
      />
      {errors.managerSignature && <Typography color="error" variant="caption">{errors.managerSignature}</Typography>}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} disabled={disabled || loading}>
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default UniformForm;
